import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Package, ArrowRight, Calendar, Archive, ArchiveRestore } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  "Not Started":     { bg: "bg-slate-100 dark:bg-slate-700", text: "text-slate-600 dark:text-slate-300", dot: "bg-slate-400", bar: "bg-slate-400" },
  "Discovery":       { bg: "bg-purple-50 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-300", dot: "bg-purple-500", bar: "bg-purple-500" },
  "In Development":  { bg: "bg-blue-50 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300", dot: "bg-blue-500", bar: "bg-blue-500" },
  "UAT":             { bg: "bg-amber-50 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-300", dot: "bg-amber-500", bar: "bg-amber-500" },
  "Delivered":       { bg: "bg-green-50 dark:bg-green-900/30", text: "text-green-700 dark:text-green-300", dot: "bg-green-500", bar: "bg-green-500" },
  "On Hold":         { bg: "bg-orange-50 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-300", dot: "bg-orange-500", bar: "bg-orange-500" },
  "Cancelled":       { bg: "bg-red-50 dark:bg-red-900/30", text: "text-red-700 dark:text-red-300", dot: "bg-red-500", bar: "bg-red-500" },
};

export function MyProductsPanel({ onSelectProduct }) {
  const { user, selectProduct, token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchProducts();
    }
  }, [token]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/my-products`);
      setProducts(res.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    selectProduct(product);
    if (onSelectProduct) {
      onSelectProduct(product);
    }
  };

  const handleStatusChange = async (e, productId, newStatus) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API_URL}/api/products/${productId}/status`, { status: newStatus });
      setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, status: newStatus } : p)));
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const [filterStatus, setFilterStatus] = useState("All");

  const handleArchive = async (e, productId, archived) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API_URL}/api/products/${productId}/archive`, { archived });
      setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, archived } : p)));
    } catch (err) {
      console.error("Failed to archive:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D4C8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="my-products-panel">
      <div>
        <p className="text-xs font-semibold tracking-widest text-[#00D4C8] uppercase mb-2">Product Selection</p>
        <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">My Products</h1>
        <p className="text-slate-500 mt-1">Select a product to manage its lifecycle</p>
      </div>

      {products.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="status-filter-bar">
          {["All", ...Object.keys(statusConfig), "Archived"].map((s) => {
            const isActive = filterStatus === s;
            let count;
            if (s === "All") count = products.filter(p => !p.archived).length;
            else if (s === "Archived") count = products.filter(p => p.archived).length;
            else count = products.filter(p => !p.archived && (p.status || "Not Started") === s).length;
            if (s !== "All" && s !== "Archived" && count === 0) return null;
            if (s === "Archived" && count === 0) return null;
            const cfg = s === "All" || s === "Archived" ? null : statusConfig[s];
            return (
              <button key={s} onClick={() => setFilterStatus(s)} data-testid={`filter-status-${s.toLowerCase().replace(/ /g, '-')}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  isActive
                    ? (s === "Archived" ? "bg-slate-700 text-white border-slate-700" : cfg ? `${cfg.bg} ${cfg.text} border-current ring-2 ring-current/20` : "bg-[#00D4C8] text-white border-[#00D4C8]")
                    : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border-slate-200 dark:border-slate-500 hover:border-slate-300 dark:hover:border-slate-400"
                }`}>
                {s === "Archived" && <Archive className="w-3 h-3" />}
                {cfg && <span className={`w-2 h-2 rounded-full ${cfg.dot} inline-block`} />}
                {s} <span className="opacity-60">({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {products.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Products Assigned</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            {user?.role === 'admin' 
              ? "No products have been created yet. Go to Admin Panel to create your first product."
              : "You haven't been assigned to any products yet. Contact your administrator to get access."}
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {(filterStatus === "Archived"
            ? products.filter(p => p.archived)
            : filterStatus === "All"
              ? products.filter(p => !p.archived)
              : products.filter(p => !p.archived && (p.status || "Not Started") === filterStatus)
          ).map((product, idx) => (
            <Card 
              key={product.id} 
              className={`group cursor-pointer card-modern overflow-hidden card-enter ${product.archived ? "opacity-70" : "hover:border-[#00D4C8]/50"}`}
              style={{ animationDelay: `${idx * 80}ms` }}
              onClick={() => handleSelectProduct(product)}
              data-testid={`product-card-${product.id}`}
            >
              <CardContent className="p-0">
                <div className={`h-1.5 ${(statusConfig[product.status] || statusConfig["Not Started"]).bar} group-hover:h-2 transition-all duration-300`} />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00D4C8]/10 to-[#39D98A]/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Package className="w-6 h-6 text-[#00D4C8]" />
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => handleArchive(e, product.id, !product.archived)}
                        className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center hover:bg-amber-50 transition-colors"
                        title={product.archived ? "Unarchive" : "Archive"}
                        data-testid={`product-archive-${product.id}`}
                      >
                        {product.archived
                          ? <ArchiveRestore className="w-4 h-4 text-amber-500" />
                          : <Archive className="w-4 h-4 text-slate-300 hover:text-amber-500" />}
                      </button>
                      <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-[#00D4C8]/10 transition-colors">
                        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#00D4C8] group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2 group-hover:text-[#00D4C8] transition-colors font-['Outfit']">
                    {product.name}
                  </h3>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-4 leading-relaxed">
                    {product.description || "No description available"}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-400 pt-3 border-t border-slate-100 dark:border-slate-700">
                    <span className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded-md">
                      <Calendar className="w-3 h-3" />
                      {new Date(product.created_at).toLocaleDateString()}
                    </span>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Select value={product.status || "Not Started"} onValueChange={(val) => handleStatusChange({ stopPropagation: () => {} }, product.id, val)}>
                        <SelectTrigger className={`h-7 text-[11px] font-semibold border-0 gap-1.5 px-2.5 rounded-full ${(statusConfig[product.status] || statusConfig["Not Started"]).bg} ${(statusConfig[product.status] || statusConfig["Not Started"]).text}`} data-testid={`product-status-${product.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                          {Object.keys(statusConfig).map((s) => (
                            <SelectItem key={s} value={s} className="dark:text-slate-100 dark:hover:bg-slate-700">
                              <span className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${statusConfig[s].dot} inline-block`} />
                                {s}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
